import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import { ListChecks, Info } from "lucide-react";

const QUANTITY_TOLERANCE = 0.001;

const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getLocalDate = () => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60 * 1000);
  return localTime.toISOString().slice(0, 10);
};

const getPackKey = (pack) => [
  pack.name || '',
  toFiniteNumber(pack.pack_size),
  toFiniteNumber(pack.rate),
  toFiniteNumber(pack.gst),
].join('_');

const getSelectedQuantity = (packs) => packs.reduce((sum, pack) => (
  sum + (toFiniteNumber(pack.pack_size) === 0
    ? toFiniteNumber(pack.loose_qty)
    : toFiniteNumber(pack.pack_size))
), 0);

const getInitialPackSelection = (item) => {
  const savedSelection = Array.isArray(item.selectedPacksData)
    ? item.selectedPacksData.filter((pack) => (
        toFiniteNumber(pack.pack_size) > 0 || toFiniteNumber(pack.loose_qty) > 0
      ))
    : [];

  if (getSelectedQuantity(savedSelection) > QUANTITY_TOLERANCE) {
    return savedSelection;
  }

  const availablePacks = (item.brandPacks || []).map((pack) => ({
    ...pack,
    per_unit: toFiniteNumber(pack.pack_size) === 0
      ? toFiniteNumber(pack.per_unit, toFiniteNumber(pack.rate))
      : toFiniteNumber(pack.rate) / toFiniteNumber(pack.pack_size, 1),
  }));

  if (!availablePacks.length) return [];

  const firstPack = availablePacks.reduce((lowest, pack) => (
    pack.per_unit < lowest.per_unit ? pack : lowest
  ), availablePacks[0]);
  const orderedQuantity = toFiniteNumber(item.orderQuantity);

  if (toFiniteNumber(firstPack.pack_size) === 0) {
    return orderedQuantity > 0 ? [{
      ...firstPack,
      loose_qty: orderedQuantity,
      loose_qty_input: String(orderedQuantity),
    }] : [];
  }

  const packCount = Math.floor((orderedQuantity + QUANTITY_TOLERANCE) / firstPack.pack_size);
  return packCount > 0 ? Array.from({ length: packCount }, () => ({ ...firstPack })) : [];
};

const DispatchOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dispatchData, setDispatchData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [eWayBill, setEWayBill] = useState(false);
  const [packPopupItem, setPackPopupItem] = useState(null);
  const [selectedPacks, setSelectedPacks] = useState([]);
  const [remarksPopupItem, setRemarksPopupItem] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchOrderDetails = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const token = localStorage.getItem('authToken');

        if (!token) {
          throw new Error('Your session is unavailable. Please login again.');
        }

        const response = await fetch(`${API_BASE_URL}/indent/${id}/detail`, {
          method: 'GET',
          signal: controller.signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const result = await response.json();

        if (!response.ok || result.error) {
          throw new Error(result.message || 'Unable to load the order details.');
        }

        const masterResponse = await fetch(`${API_BASE_URL}/item/all/list`, {
          method: 'GET',
          signal: controller.signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        const masterResult = await masterResponse.json();

        if (!masterResponse.ok || masterResult.error) {
          throw new Error(masterResult.message || 'Unable to load the item master.');
        }

        const masterItems = Array.isArray(masterResult.data)
          ? masterResult.data
          : Array.isArray(masterResult)
            ? masterResult
            : [];

        {
          const details = Array.isArray(result.data)
            ? result.data
            : Array.isArray(result)
              ? result
              : [];
          setOrderDetails(details);

          if (details && details.length > 0) {
            const initialDispatchData = details.map(item => {
              const masterItem = masterItems.find(m => String(m.id) === String(item.id));

              let packSize = 0;
              let packRate = 0;
              let brandName = item.brand;
              let brandPacks = [];
              let selectedPackGst = toFiniteNumber(item.gst);
              if (masterItem && masterItem.brands && masterItem.brands.length > 0) {
                const allPacks = masterItem.brands
                  .filter(b => b && (typeof b.pack_size !== "undefined" || typeof b.pack !== "undefined") && b.rate !== undefined)
                  .map(b => {
                    const packSize = Number(b.pack_size ?? b.pack ?? 0);
                    const rate = Number(b.rate);
                    const perUnit = Number(b.per_unit ?? (packSize > 0 ? rate / packSize : NaN));
                    return {
                      name: (b.name || '').trim(),
                      pack_size: packSize,
                      rate,
                      per_unit: perUnit,
                      gst: toFiniteNumber(b.gst)
                    };
                  })
                  .filter(pack => (
                    Number.isFinite(pack.pack_size) &&
                    pack.pack_size >= 0 &&
                    Number.isFinite(pack.rate) &&
                    pack.rate >= 0 &&
                    Number.isFinite(pack.per_unit) &&
                    pack.per_unit >= 0
                  ));

                if (!item.brand || item.brand === null) {
                  if (allPacks.length === 1) {
                    packSize = allPacks[0].pack_size;
                    packRate = allPacks[0].rate;
                    brandName = allPacks[0].name;
                    selectedPackGst = allPacks[0].gst;
                    brandPacks = [];
                  } else {
                    brandPacks = allPacks;
                    if (allPacks.length > 0) {
                      const lowestCostPack = allPacks.reduce((min, curr) =>
                        curr.per_unit < min.per_unit ? curr : min
                        , allPacks[0]);
                      packSize = lowestCostPack.pack_size;
                      packRate = lowestCostPack.rate;
                      brandName = null;
                      selectedPackGst = lowestCostPack.gst;
                    }
                  }
                } else {
                  const uniqueBrands = new Set(
                    allPacks
                      .map(p => p.name.trim())
                      .filter(name => name !== '')
                  );

                  const selectedBrand = (item.brand || '').trim();
                  const packsForBrand = selectedBrand
                    ? allPacks.filter(p => p.name.trim().toLowerCase() === selectedBrand.toLowerCase())
                    : allPacks;

                  console.log(`📦 Item: ${item.name}`);
                  console.log(`🏷️ Selected Brand: "${selectedBrand}"`);
                  console.log(`📊 Unique Brands Count: ${uniqueBrands.size}`);
                  console.log(`✅ Packs for "${selectedBrand}":`, packsForBrand);

                  if (uniqueBrands.size > 1 || packsForBrand.length > 1) {
                    // If the store has picked a specific brand, only show packs for that brand
                    brandPacks = selectedBrand ? packsForBrand : allPacks;
                    if (packsForBrand.length > 0) {
                      const lowestCostPack = packsForBrand.reduce((min, curr) =>
                        curr.per_unit < min.per_unit ? curr : min
                        , packsForBrand[0]);
                      packSize = lowestCostPack.pack_size;
                      packRate = lowestCostPack.rate;
                      brandName = selectedBrand;
                      selectedPackGst = lowestCostPack.gst;
                    }
                  } else if (packsForBrand.length === 1) {
                    packSize = packsForBrand[0].pack_size;
                    packRate = packsForBrand[0].rate;
                    brandName = selectedBrand;
                    selectedPackGst = packsForBrand[0].gst;
                    brandPacks = [];
                  } else {
                    packSize = 0;
                    packRate = 0;
                    brandName = selectedBrand;
                    brandPacks = [];
                  }
                }
              }

              const orderQty = toFiniteNumber(item.qty);
              let dispatchQty = orderQty;
let remarks = "";
let indentRemarks = item.remarks || "";

              if (packSize > 0) {
                const totalPacks = Math.floor(orderQty / packSize);
                const maxDispatch = totalPacks * packSize;
                dispatchQty = maxDispatch;
              }

              return {
                id: item.id,
                name: item.name,
                brand: brandName,
                unit: item.unit,
                originalBrand: item.brand,
                category: masterItem?.category || item.category,
                orderQuantity: orderQty,
                dispatchQuantity: dispatchQty,
                pack_size: packSize,
                no_of_packs: packSize > 0 ? Math.floor(dispatchQty / packSize) : "-",
                rate: packRate || (() => {
                  if (brandPacks.length > 0) {
                    const validPacks = brandPacks.filter(p => p.pack_size > 0);
                    if (validPacks.length > 0) {
                      const lowestPerUnit = Math.min(...validPacks.map(p => p.per_unit));
                      return lowestPerUnit.toFixed(2);
                    }
                  }
                  return toFiniteNumber(masterItem?.rate, toFiniteNumber(item.rate, toFiniteNumber(item.rate_range)));
                })(),
              remarks: remarks,
indentRemarks: indentRemarks,
                brandPacks,
                gst: selectedPackGst,
              };
            });

            setDispatchData(initialDispatchData);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error:', err);
          setOrderDetails([]);
          setDispatchData([]);
          setErrorMessage(err.message || 'Unable to load this dispatch order.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    if (id) {
      fetchOrderDetails();
    }

    return () => controller.abort();
  }, [id]);


  const handleDispatchQuantityChange = (itemId, rawValue) => {
    setDispatchData(prev =>
      prev.map(item => {
        if (item.id !== itemId) return item;

        const pack_size = Number(item.pack_size);
        const orderQty = Number(item.orderQuantity);
        const isBlank = rawValue === '';
        const inputQty = parseFloat(rawValue);

        if (isBlank || isNaN(inputQty)) {
          return { ...item, dispatchQuantity: '', no_of_packs: '-' };
        }

        if (inputQty < 0) {
          return { ...item, dispatchQuantity: 0, no_of_packs: pack_size > 0 ? 0 : '-' };
        }

        if (pack_size === 0) {
          return {
            ...item,
            dispatchQuantity: Math.min(inputQty, orderQty),
            no_of_packs: "-",
          };
        }

        let roundedQty;
        const lower = Math.floor(inputQty / pack_size) * pack_size;
        const higher = Math.ceil(inputQty / pack_size) * pack_size;

        roundedQty = (Math.abs(higher - inputQty) < Math.abs(inputQty - lower)) ? higher : lower;
        if (roundedQty > orderQty) {
          roundedQty = Math.floor(orderQty / pack_size) * pack_size;
        }

        return {
          ...item,
          dispatchQuantity: roundedQty,
          no_of_packs: Math.floor((roundedQty || 0) / pack_size),
        };
      })
    );
  };
const isExceedingOrder = () => {
  if (!packPopupItem) return false;
  const totalSelected = getSelectedQuantity(selectedPacks);
  return totalSelected > (toFiniteNumber(packPopupItem.orderQuantity) + QUANTITY_TOLERANCE);
};
  const handleRemarksChange = (itemId, value) => {
    setDispatchData(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, remarks: value }
          : item
      )
    );
  };

  const handleLooseQuantityChange = (pack, rawValue) => {
    if (!packPopupItem || !/^\d*(?:\.\d{0,3})?$/.test(rawValue)) {
      return;
    }

    const packKey = getPackKey(pack);
    const otherSelected = selectedPacks.filter(selected => getPackKey(selected) !== packKey);
    const otherQuantity = getSelectedQuantity(otherSelected);
    const remainingQuantity = Math.max(
      0,
      toFiniteNumber(packPopupItem.orderQuantity) - otherQuantity
    );
    const parsedValue = rawValue === '' ? 0 : toFiniteNumber(rawValue);
    const quantity = Math.min(parsedValue, remainingQuantity);
    const inputValue = parsedValue > remainingQuantity
      ? String(Number(remainingQuantity.toFixed(3)))
      : rawValue;
    const updatedPacks = inputValue === ''
      ? otherSelected
      : [
          ...otherSelected,
          {
            ...pack,
            pack_size: 0,
            loose_qty: quantity,
            loose_qty_input: inputValue,
          },
        ];
    const totalQuantity = otherQuantity + quantity;

    setSelectedPacks(updatedPacks);
    setDispatchData(prev =>
      prev.map(item =>
        item.id === packPopupItem.id
          ? { ...item, dispatchQuantity: totalQuantity }
          : item
      )
    );
  };


  const handleSubmitDispatch = async () => {
    const orderItems = dispatchData.filter(item => toFiniteNumber(item.orderQuantity) > 0);

    for (const item of orderItems) {
      const qty = Number(item.dispatchQuantity);
      const orderQty = toFiniteNumber(item.orderQuantity);

      if (item.dispatchQuantity === '' || !Number.isFinite(qty) || qty < 0) {
        alert(`Invalid dispatch quantity for item "${item.name}".`);
        return;
      }

      if (qty > orderQty + QUANTITY_TOLERANCE) {
        alert(`Dispatch quantity for "${item.name}" cannot exceed the ordered quantity (${orderQty.toFixed(3)}).`);
        return;
      }

      const amount = calcItemAmount(item);
      if (qty > 0 && (!Number.isFinite(amount) || amount <= 0)) {
        alert(`A valid rate and amount are required for item "${item.name}".`);
        return;
      }
    }

    const itemsToDispatch = orderItems.filter(item => toFiniteNumber(item.dispatchQuantity) > 0);

    if (itemsToDispatch.length === 0) {
      alert('Enter a dispatch quantity for at least one item.');
      return;
    }

    if (!/^\d{10}$/.test(driverPhone)) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }

    setSubmitting(true);
    let dispatchSucceeded = false;

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Your session is unavailable. Please login again.');
      }
      const response = await fetch(`${API_BASE_URL}/indent/${id}/dispatch`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dispatch: getLocalDate(),
          vehicleNumber: vehicleNumber.trim(),
          vehicleType: vehicleType.trim(),
          driverName: driverName.trim(),
          driverPhone,
          bill: eWayBill ? "yes" : "no",
          items: itemsToDispatch
            .map(item => {
              const gst = item.gst || 0; // ✅ USE ITEM-LEVEL GST FROM INDENT API
              let packsString = "";

              // Case 1: User manually selected packs via popup
              if (item.selectedPacksData && item.selectedPacksData.length > 0) {
                const grouped = {};
                item.selectedPacksData.forEach(p => {
                  const key = getPackKey(p);
                  if (!grouped[key]) {
               grouped[key] = {
    name: p.name || '',
    pack_size: p.pack_size,
    rate: p.rate,
    gst: p.gst ?? 0,
    count: 0,
    loose_qty: 0
};
                  }
                  if (p.pack_size === 0) {
                    grouped[key].loose_qty += toFiniteNumber(p.loose_qty);
                  } else {
                    grouped[key].count += 1;
                  }
                });

                packsString = Object.values(grouped)
                  .map(g => {
                    if (g.pack_size === 0) {
                      return `${g.name}>>0>>${g.loose_qty}>>${g.rate}>>${g.gst}`;
                    } else {
                      return `${g.name}>>${g.pack_size}>>${g.rate}>>${g.count}>>${g.gst}`;
                    }
                  })
                  .join('||');
              }
              // Case 2: Auto-calculated packs
              else if (item.pack_size > 0) {
                const packCount = Math.floor(parseFloat(item.dispatchQuantity) / item.pack_size);
                const brandName = item.brand || '';
                packsString = `${brandName}>>${item.pack_size}>>${item.rate}>>${packCount}>>${gst}`;
              }
              // Case 3: Loose quantity
              else {
                const brandName = item.brand || '';
                const looseQty = parseFloat(item.dispatchQuantity);
                packsString = `${brandName}>>0>>${looseQty}>>${item.rate}>>${gst}`;
              }

              // ✅ Calculate total amount WITH GST using item.gst
              const dispatchQty = parseFloat(item.dispatchQuantity || 0);
              let totalAmount = 0;

              if (item.selectedPacksData && item.selectedPacksData.length > 0) {
                totalAmount = item.selectedPacksData.reduce((sum, p) => {
                  const lineGst = p.gst ?? gst;
                  if (p.pack_size === 0) {
                    const perUnit = toFiniteNumber(p.per_unit, toFiniteNumber(p.rate));
                    return sum + (perUnit * toFiniteNumber(p.loose_qty) * (1 + lineGst / 100));
                  } else {
                    return sum + (p.rate * (1 + lineGst / 100));
                  }
                }, 0);
              } else if (item.pack_size > 0 && item.rate) {
                const numPacks = dispatchQty / item.pack_size;
                const baseAmount = numPacks * parseFloat(item.rate);
                totalAmount = baseAmount * (1 + gst / 100);
              } else {
                totalAmount = dispatchQty * parseFloat(item.rate || 0) * (1 + gst / 100);
              }

              const isDailieItem = item.category && item.category.toLowerCase() === 'dailie';

              const payload = {
                id: item.id,
                qty: parseFloat(item.dispatchQuantity).toFixed(3),
                remarks: item.remarks,
                rate: (() => {
                  if (item.selectedPacksData && item.selectedPacksData.length > 0) {
                    const totalBase = item.selectedPacksData.reduce((sum, p) =>
                      sum + (p.pack_size === 0
                        ? toFiniteNumber(p.per_unit, toFiniteNumber(p.rate)) * toFiniteNumber(p.loose_qty)
                        : toFiniteNumber(p.rate)), 0
                    );
                    const dispatchQty = parseFloat(item.dispatchQuantity) || 1;
                    return (totalBase / dispatchQty).toFixed(2);
                  } else if (item.pack_size > 0) {
                    return (item.rate / item.pack_size).toFixed(2);
                  } else {
                    return parseFloat(item.rate || 0).toFixed(2);
                  }
                })(),
                amount: totalAmount.toFixed(2)
              };

              if (!isDailieItem) {
                payload.packs = packsString;
              }

              return payload;
            }),
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && !result.error) {
        dispatchSucceeded = true;
        setShowPopup(false);
        setSuccessMessage('Order dispatched successfully!');
        setTimeout(() => navigate('/indent-request'), 1500);
      } else {
        const message = result.message || 'Error dispatching order';
        setErrorMessage(message);
        alert(message);
      }
    } catch (err) {
      console.error('Error dispatching order:', err);
      const message = err.message || 'Error dispatching order. Please try again.';
      setErrorMessage(message);
      alert(message);
    } finally {
      if (!dispatchSucceeded) {
        setSubmitting(false);
      }
    }
  };

  const handleBackToOrders = () => {
    navigate('/indent-request');
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 bg-gray-50 rounded-lg">
        {successMessage && (
          <div className="mb-3 px-4 py-2 bg-green-100 text-green-800 rounded text-sm font-medium">
            {successMessage}
          </div>
        )}
        <div className="bg-white shadow rounded-lg p-6 h-full flex flex-col">
          <div className="text-center py-8">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  const dispatchSummary = dispatchData.reduce(
    (summary, item) => {
      const qty = parseFloat(item.dispatchQuantity || 0);
      const packs = item.pack_size > 0 ? Math.floor(qty / item.pack_size) : 0;
      if (qty > 0) {
        return {
          totalItems: summary.totalItems + 1,
          totalQty: summary.totalQty + qty,
          totalPacks: summary.totalPacks + packs,
        };
      }
      return summary;
    },
    { totalItems: 0, totalQty: 0, totalPacks: 0 }
  );

  // ✅ UNIFIED amount calculation helper using item.gst
  const calcItemAmount = (item) => {
    const dispatchQty = toFiniteNumber(item.dispatchQuantity);
    const gst = toFiniteNumber(item.gst);

    if (item.selectedPacksData && item.selectedPacksData.length > 0) {
      return item.selectedPacksData.reduce((sum, p) => {
        const lineGst = toFiniteNumber(p.gst ?? gst);
        if (toFiniteNumber(p.pack_size) === 0) {
          const perUnit = toFiniteNumber(p.per_unit, toFiniteNumber(p.rate));
          return sum + (perUnit * toFiniteNumber(p.loose_qty) * (1 + lineGst / 100));
        } else {
          return sum + (toFiniteNumber(p.rate) * (1 + lineGst / 100));
        }
      }, 0);
    }

    if (item.pack_size > 0 && item.rate) {
      const numPacks = dispatchQty / item.pack_size;
      const baseAmount = numPacks * parseFloat(item.rate);
      return baseAmount * (1 + gst / 100);
    }

    return dispatchQty * parseFloat(item.rate || 0) * (1 + gst / 100);
  };

  const hasInvalidAmounts = dispatchData.some(item => {
    const dispatchQty = toFiniteNumber(item.dispatchQuantity);
    if (dispatchQty === 0) return false;
    const amount = calcItemAmount(item);
    return (
      dispatchQty < 0 ||
      dispatchQty > toFiniteNumber(item.orderQuantity) + QUANTITY_TOLERANCE ||
      !Number.isFinite(amount) ||
      amount <= 0
    );
  });
  const hasNoDispatchItems = !dispatchData.some(item => (
    toFiniteNumber(item.orderQuantity) > 0 &&
    toFiniteNumber(item.dispatchQuantity) > 0
  ));

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 rounded-lg">
      {successMessage && (
        <div className="mb-3 px-4 py-2 bg-green-100 text-green-800 rounded text-sm font-medium">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-3 px-4 py-2 bg-red-100 text-red-800 rounded text-sm font-medium">
          {errorMessage}
        </div>
      )}
      <div className="bg-white shadow rounded-lg p-6 h-full flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Dispatch Order #{id}</h1>
          <button
            onClick={handleBackToOrders}
            className="px-4 py-2 bg-sky-600 text-white rounded hover:bg-sky-700 transition flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Orders
          </button>
        </div>

        <div className="mb-4 flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-900 text-sm rounded-lg px-4 py-3">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
          <span>
            Please note that for customers who have opted for lowest price on a given product, the different pack sizes in the lowest price product will only be shown for dispatch.
          </span>
        </div>

        {/* Order Items Dispatch Table */}
        {orderDetails && orderDetails.length > 0 ? (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Items to Despatch</h3>
            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">S.No</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Item Name</th>
                    <th className="border border-gray-300 px-6 py-2 text-center w-32">Ordered</th>
                    <th className="border border-gray-300 px-4 py-2 text-center">Despatch</th>
                    <th className="border border-gray-300 px-4 py-2 text-center">Diff</th>
                    <th className="border border-gray-300 px-4 py-2 text-center">Amount</th>
                    <th className="border border-gray-300 px-4 py-2 text-left min-w-[180px]">Remarks</th>
<th className="border border-gray-300 px-2 py-2 text-center w-44">Despatch Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatchData
                    .filter(item => Number(item.orderQuantity || 0) > 0)
                    .map((item, index) => {
                      const dispatchQty = Number(item.dispatchQuantity || 0);
                      const orderQty = Number(item.orderQuantity || 0);
                      const diff = dispatchQty - orderQty;

                      return (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-4 py-2">{index + 1}.</td>

                          <td className="border border-gray-300 px-4 py-2 font-medium">
                            <div className="text-gray-800 flex items-center gap-2 flex-wrap">
                              <span>{item.name}</span>
                              <span className="text-xs font-semibold text-gray-700">
                                {item.unit}
                              </span>
                              {item.originalBrand && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-green-100 text-green-700">
                                  {item.originalBrand}
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="border border-gray-300 px-6 py-2 text-right">
                            {orderQty.toFixed(3)}
                          </td>

                          <td className="border border-gray-300 px-4 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={item.dispatchQuantity ?? ''}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setDispatchData(prev =>
                                    prev.map(data =>
                                      data.id === item.id ? { ...data, dispatchQuantity: raw } : data
                                    )
                                  );
                                }}
                                onBlur={(e) => handleDispatchQuantityChange(item.id, e.target.value)}
                                disabled={item.brandPacks && item.brandPacks.length > 1}
                                className={`no-spinner w-20 px-2 py-1 border border-gray-300 rounded text-center ${item.brandPacks && item.brandPacks.length >= 1 && (item.originalBrand === null || item.brandPacks.length > 1)
                                  ? 'bg-gray-100 cursor-not-allowed opacity-60'
                                  : ''
                                  }`}
                              />

                              {item.brandPacks && item.brandPacks.length > 1 &&
                                orderDetails?.[0]?.depatment !== "Commissionerate of Adi Dravidar Welfare" && (
                                  <button
                                    className="p-1 rounded bg-blue-100 hover:bg-blue-200"
                                    title="Select pack size"
                                    onClick={() => {
                                      setPackPopupItem(item);
                                      setSelectedPacks(getInitialPackSelection(item));
                                    }}
                                  >
                                    <ListChecks size={20} className="text-blue-700" />
                                  </button>
                                )}
                            </div>
                          </td>

                          <td className="border border-gray-300 px-2 py-2 text-center font-semibold">
                            <span className={diff < 0 ? "text-red-600" : diff > 0 ? "text-green-600" : "text-gray-700"}>
                              {diff.toFixed(2)}
                            </span>
                          </td>

                          <td className="border border-gray-300 px-4 py-2 text-center font-semibold text-green-700">
                            <div className="flex items-center justify-center gap-2">
                              <span>₹{calcItemAmount(item).toFixed(2)}</span>

                              {parseFloat(item.dispatchQuantity || 0) > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setRemarksPopupItem(item)}
                                  className="p-1 rounded bg-indigo-100 hover:bg-indigo-200"
                                  title="View dispatch details"
                                >
                                  <Info size={18} className="text-indigo-700" />
                                </button>
                              )}
                            </div>
                          </td>

<td className="border border-gray-300 px-4 py-2 text-sm text-gray-600 italic min-w-[180px] max-w-[250px] whitespace-normal break-words">
  {item.indentRemarks || '—'}
</td>
<td className="border border-gray-300 px-2 py-2 text-center w-44">
  <div className="flex items-center gap-2">
    <input
      type="text"
      value={item.remarks}
                                onChange={(e) => handleRemarksChange(item.id, e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter remarks (optional)"
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              {/* Total Amount Summary Row */}
              <div className="mt-4 flex justify-end">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg px-6 py-3 shadow-sm">
                  <div className="flex items-center gap-4">
                    <span className="text-base font-semibold text-gray-700">Total Amount:</span>
                    <span className="text-2xl font-bold text-green-700">
                      ₹{dispatchData
                        .filter(item => Number(item.orderQuantity || 0) > 0)
                        .reduce((sum, item) => sum + calcItemAmount(item), 0)
                        .toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center text-gray-500 mb-6">
            <p>No items found for this order</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4">
          <button
            onClick={handleBackToOrders}
            className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
          >
            Cancel
          </button>
          <button
            onClick={() => setShowPopup(true)}
            disabled={submitting || hasInvalidAmounts || hasNoDispatchItems}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title={
              hasInvalidAmounts
                ? "Cannot dispatch: Check quantities, rates, and amounts"
                : hasNoDispatchItems
                  ? "Enter a dispatch quantity for at least one item"
                  : ""
            }
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Despatching...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Confirm Despatch
              </>
            )}
          </button>
        </div>

        {/* Dispatch Summary Popup */}
        {showPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">Dispatch Order Summary</h2>
                  <button
                    onClick={() => setShowPopup(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2">
                    <h3 className="text-base font-semibold text-blue-800">
                      Order Summary for Items to be Dispatched
                    </h3>
                    <div className="bg-white px-3 py-1 rounded-md text-xs border border-blue-200">
                      <span className="font-semibold text-gray-900 ml-1">
                        {new Date().toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white bg-opacity-70 rounded-md p-3 border border-blue-100">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700 text-sm">Total Items:</span>
                        <span className="font-bold text-gray-900">{dispatchSummary.totalItems}</span>
                      </div>
                    </div>

                    <div className="bg-white bg-opacity-70 rounded-md p-3 border border-blue-100">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700 text-sm">Total Quantity:</span>
                        <span className="font-bold text-gray-900">{dispatchSummary.totalQty.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="bg-white bg-opacity-70 rounded-md p-3 border border-blue-100">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700 text-sm">Total Packs:</span>
                        <span className="font-bold text-gray-900">{dispatchSummary.totalPacks}</span>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-md p-3 border border-green-200">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700 text-sm">Est. Value:</span>
                        <span className="font-bold text-green-700">
                          ₹{dispatchData
                            .filter(item => Number(item.orderQuantity || 0) > 0)
                            .reduce((sum, item) => sum + calcItemAmount(item), 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vehicle & Incharge Details */}
                <div className="space-y-3">
                  <h4 className="text-base font-semibold text-gray-800">Vehicle & Incharge Details</h4>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Vehicle Number</label>
                        <input
                          type="text"
                          value={vehicleNumber}
                          onChange={(e) => setVehicleNumber(e.target.value)}
                          className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                          placeholder="e.g., TN 01 AB 1234"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Vehicle Type</label>
                        <input
                          type="text"
                          value={vehicleType}
                          onChange={(e) => setVehicleType(e.target.value)}
                          className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                          placeholder="e.g., Truck / Van"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Incharge Name</label>
                        <input
                          type="text"
                          value={driverName}
                          onChange={(e) => setDriverName(e.target.value)}
                          className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                          placeholder="e.g., Ravi Kumar"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Incharge Phone</label>
                        <input
                          type="tel"
                          value={driverPhone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                            setDriverPhone(value);
                          }}
                          className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                          placeholder="e.g., 9876543210"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* E-Way Bill */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={eWayBill}
                      onChange={(e) => setEWayBill(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-800 font-medium text-sm">E-Way Bill Required</span>
                  </label>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowPopup(false)}
                  className="px-5 py-2 text-sm font-semibold bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitDispatch}
                  disabled={submitting}
                  className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all shadow-md flex items-center gap-2 ${submitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800'
                    }`}
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Submitting...
                    </>
                  ) : (
                    <>Submit Dispatch</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pack Selection Popup */}
        {packPopupItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold mb-2">{packPopupItem.name}</h2>
                    <div className="flex justify-between items-center">
                      <div>
                        {packPopupItem.brand ? (
                          <span className="text-sm font-semibold px-3 py-1 rounded bg-green-100 text-green-700">
                            {packPopupItem.brand}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500 italic">No brand selected</span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-gray-600">Ordered: </span>
                        <span className="font-bold text-lg text-gray-900">{packPopupItem.orderQuantity}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setPackPopupItem(null)}
                    className="text-gray-400 hover:text-gray-600 ml-4"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Available Pack Sizes:</h3>

                {(() => {
                  const packsWithPerUnit = packPopupItem.brandPacks
                    .map(p => ({
                      ...p,
                      per_unit: p.pack_size === 0 ? p.per_unit : (p.rate / p.pack_size)
                    }));
                  const lowestPack = packsWithPerUnit
                    .reduce((min, curr) => curr.per_unit < min.per_unit ? curr : min, packsWithPerUnit[0]);

                  return (
                    <>
                      <div className="space-y-2">
                        {packPopupItem.brandPacks
                          .filter(p => p.pack_size === lowestPack.pack_size && p.name === lowestPack.name && p.rate === lowestPack.rate)
                          .map((p, idx) => {
                            const ratePerKg = p.pack_size === 0
                              ? Number(p.per_unit).toFixed(2)
                              : (p.rate / p.pack_size).toFixed(2);

                            return (
                              <div key={idx} className="flex justify-between items-center border border-gray-200 p-3 rounded-lg hover:bg-gray-50 transition">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-gray-800">
                                      {p.pack_size === 0 ? (
                                        <span>Loose Qty</span>
                                      ) : (
                                        <span>
                                          {p.pack_size < 1
                                            ? `${(p.pack_size * 1000).toFixed(0)} G`
                                            : `${p.pack_size} ${packPopupItem.unit}`
                                          }
                                        </span>
                                      )}
                                    </span>
                                    {!packPopupItem.brand && (
                                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                        {p.name}
                                      </span>
                                    )}
                                    <span className="text-sm font-bold px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                                      ₹{p.rate}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    Per {packPopupItem.unit}: ₹{ratePerKg}
                                    {p.gst > 0 && p.pack_size > 0 && (
                                      <span className="text-orange-700 font-semibold ml-2">
                                        | GST: {p.gst}%
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {p.pack_size > 0 ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 font-bold"
                                      onClick={() => {
                                        const index = selectedPacks.findIndex(x => getPackKey(x) === getPackKey(p));
                                        if (index >= 0) {
                                          const updated = [...selectedPacks];
                                          updated.splice(index, 1);
                                          const newTotal = getSelectedQuantity(updated);
                                          setSelectedPacks(updated);
                                          setDispatchData(prev =>
                                            prev.map(itm =>
                                              itm.id === packPopupItem.id ? { ...itm, dispatchQuantity: newTotal } : itm
                                            )
                                          );
                                        }
                                      }}
                                    >-</button>

                                    <input
                                      type="number"
                                      min="0"
                                      className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm font-semibold -ml-1"
                                      value={selectedPacks.filter(x => getPackKey(x) === getPackKey(p)).length || ''}
                                      onChange={(e) => {
                                        const currentPacks = selectedPacks.filter(x => getPackKey(x) === getPackKey(p));
                                        const otherPacks = selectedPacks.filter(x => getPackKey(x) !== getPackKey(p));
                                        const maximumCount = Math.max(0, Math.floor(
                                          (toFiniteNumber(packPopupItem.orderQuantity) - getSelectedQuantity(otherPacks) + QUANTITY_TOLERANCE) /
                                          toFiniteNumber(p.pack_size, 1)
                                        ));
                                        const newCount = Math.min(Math.max(parseInt(e.target.value) || 0, 0), maximumCount);
                                        const diff = newCount - currentPacks.length;
                                        if (diff > 0) {
                                          const newPacks = Array(diff).fill(p);
                                          const updatedPacks = [...selectedPacks, ...newPacks];
                                          const newTotal = getSelectedQuantity(updatedPacks);
                                          if (newTotal > toFiniteNumber(packPopupItem.orderQuantity) + QUANTITY_TOLERANCE) return;
                                          setSelectedPacks(updatedPacks);
                                          setDispatchData(prev =>
                                            prev.map(itm =>
                                              itm.id === packPopupItem.id ? { ...itm, dispatchQuantity: newTotal } : itm
                                            )
                                          );
                                        } else if (diff < 0) {
                                          const updated = [...selectedPacks];
                                          for (let i = 0; i < Math.abs(diff); i++) {
                                            const index = updated.findIndex(x => getPackKey(x) === getPackKey(p));
                                            if (index >= 0) updated.splice(index, 1);
                                          }
                                          const newTotal = getSelectedQuantity(updated);
                                          setSelectedPacks(updated);
                                          setDispatchData(prev =>
                                            prev.map(itm =>
                                              itm.id === packPopupItem.id ? { ...itm, dispatchQuantity: newTotal } : itm
                                            )
                                          );
                                        }
                                      }}
                                      placeholder="0"
                                    />

                                    <button
                                      className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-bold"
                                      onClick={() => {
                                        const currentTotal = getSelectedQuantity(selectedPacks);
                                        const newTotal = currentTotal + p.pack_size;
                                        if (newTotal > toFiniteNumber(packPopupItem.orderQuantity) + QUANTITY_TOLERANCE) return;
                                        setSelectedPacks(prev => [...prev, p]);
                                        setDispatchData(prev =>
                                          prev.map(itm =>
                                            itm.id === packPopupItem.id ? { ...itm, dispatchQuantity: newTotal } : itm
                                          )
                                        );
                                      }}
                                    >+</button>

                                    <span className="text-sm font-bold text-gray-900 ml-2 px-2 py-0.5 bg-gray-100 rounded">
                                      {(() => {
                                        const count = selectedPacks.filter(x => getPackKey(x) === getPackKey(p)).length;
                                        const totalKg = count * p.pack_size;
                                        return totalKg > 0 ? Number(totalKg.toFixed(3)).toString() : "";
                                      })()}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                      <input
                                      type="text"
                                      inputMode="decimal"
                                      placeholder="Enter qty"
                                      className="w-24 px-2 py-1 border border-gray-300 rounded"
                                      value={(() => {
                                        const selected = selectedPacks.find(x => getPackKey(x) === getPackKey(p));
                                        return selected?.loose_qty_input ?? selected?.loose_qty ?? '';
                                      })()}
                                      onChange={(e) => handleLooseQuantityChange(p, e.target.value)}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>

                      {packPopupItem.brandPacks.filter(p => !(p.pack_size === lowestPack.pack_size && p.name === lowestPack.name && p.rate === lowestPack.rate)).length > 0 && (
                        <div className="mt-4 space-y-2">
                          <h4 className="text-sm font-semibold text-gray-700">Other Brand Packs</h4>
                          {packPopupItem.brandPacks
                            .filter(p => !(p.pack_size === lowestPack.pack_size && p.name === lowestPack.name && p.rate === lowestPack.rate))
                            .map((p, idx) => {
                              const ratePerKg = p.pack_size === 0
                                ? Number(p.per_unit).toFixed(2)
                                : (p.rate / p.pack_size).toFixed(2);

                              return (
                                <div key={idx} className="flex justify-between items-center border border-gray-200 p-3 rounded-lg hover:bg-gray-50 transition">
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-semibold text-gray-800">
                                        {p.pack_size === 0 ? (
                                          <span>Loose Qty</span>
                                        ) : p.pack_size < 1 ? (
                                          `${(p.pack_size * 1000).toFixed(0)} G`
                                        ) : (
                                          `${p.pack_size} ${packPopupItem.unit}`
                                        )}
                                      </span>
                                      {!packPopupItem.brand && (
                                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                          {p.name}
                                        </span>
                                      )}
                                      <span className="text-sm font-bold px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                                        ₹{p.rate}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      Per {packPopupItem.unit}: ₹{ratePerKg}
                                      {p.gst > 0 && p.pack_size > 0 && (
                                        <span className="text-orange-700 font-semibold ml-2">
                                          | GST: {p.gst}%
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {p.pack_size > 0 ? (
                                    <div className="flex items-center gap-2">
                                      <button
                                        className="px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 font-bold"
                                        onClick={() => {
                                          const index = selectedPacks.findIndex(x => getPackKey(x) === getPackKey(p));
                                          if (index >= 0) {
                                            const updated = [...selectedPacks];
                                            updated.splice(index, 1);
                                            const newTotal = getSelectedQuantity(updated);
                                            setSelectedPacks(updated);
                                            setDispatchData(prev =>
                                              prev.map(itm =>
                                                itm.id === packPopupItem.id ? { ...itm, dispatchQuantity: newTotal } : itm
                                              )
                                            );
                                          }
                                        }}
                                      >-</button>

                                      <input
                                        type="number"
                                        min="0"
                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm font-semibold"
                                        value={selectedPacks.filter(x => getPackKey(x) === getPackKey(p)).length || ""}
                                        onChange={(e) => {
                                          const currentPacks = selectedPacks.filter(x => getPackKey(x) === getPackKey(p));
                                          const otherPacks = selectedPacks.filter(x => getPackKey(x) !== getPackKey(p));
                                          const maximumCount = Math.max(0, Math.floor(
                                            (toFiniteNumber(packPopupItem.orderQuantity) - getSelectedQuantity(otherPacks) + QUANTITY_TOLERANCE) /
                                            toFiniteNumber(p.pack_size, 1)
                                          ));
                                          const newCount = Math.min(Math.max(parseInt(e.target.value) || 0, 0), maximumCount);
                                          const currentCount = currentPacks.length;
                                          const diff = newCount - currentCount;
                                          let updated = [...selectedPacks];
                                          if (diff > 0) {
                                            for (let i = 0; i < diff; i++) updated.push(p);
                                          } else {
                                            for (let i = 0; i < Math.abs(diff); i++) {
                                              const index = updated.findIndex(x => getPackKey(x) === getPackKey(p));
                                              if (index >= 0) updated.splice(index, 1);
                                            }
                                          }
                                          const newTotal = getSelectedQuantity(updated);
                                          if (newTotal > toFiniteNumber(packPopupItem.orderQuantity) + QUANTITY_TOLERANCE) return;
                                          setSelectedPacks(updated);
                                          setDispatchData(prev =>
                                            prev.map(itm =>
                                              itm.id === packPopupItem.id ? { ...itm, dispatchQuantity: newTotal } : itm
                                            )
                                          );
                                        }}
                                        placeholder="0"
                                      />

                                      <button
                                        className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-bold"
                                        onClick={() => {
                                          const currentTotal = getSelectedQuantity(selectedPacks);
                                          const newTotal = currentTotal + p.pack_size;
                                          if (newTotal > toFiniteNumber(packPopupItem.orderQuantity) + QUANTITY_TOLERANCE) return;
                                          setSelectedPacks(prev => [...prev, p]);
                                          setDispatchData(prev =>
                                            prev.map(itm =>
                                              itm.id === packPopupItem.id ? { ...itm, dispatchQuantity: newTotal } : itm
                                            )
                                          );
                                        }}
                                      >+</button>

                                      <span className="text-sm font-bold text-gray-900 ml-2 px-2 py-0.5 bg-gray-100 rounded">
                                        {(() => {
                                          const count = selectedPacks.filter(x => getPackKey(x) === getPackKey(p)).length;
                                          const totalKg = count * p.pack_size;
                                          return totalKg > 0 ? totalKg.toFixed(3) : "";
                                        })()}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="Enter qty"
                                        className="w-24 px-2 py-1 border border-gray-300 rounded"
                                        value={(() => {
                                          const selected = selectedPacks.find(x => getPackKey(x) === getPackKey(p));
                                          return selected?.loose_qty_input ?? selected?.loose_qty ?? '';
                                        })()}
                                        onChange={(e) => handleLooseQuantityChange(p, e.target.value)}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      )}

                      {selectedPacks.length > 0 && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold text-gray-800">Selected Quantity:</span>
                            <span className="text-xl font-bold text-green-700">
                              {selectedPacks.reduce((sum, p) => {
                                if (toFiniteNumber(p.pack_size) === 0) return sum + toFiniteNumber(p.loose_qty);
                                return sum + toFiniteNumber(p.pack_size);
                              }, 0).toFixed(3)}
                            </span>
                          </div>
                          {isExceedingOrder() && (
  <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-sm text-red-700 font-semibold">
    ⚠️ Selected quantity exceeds ordered quantity!
  </div>
)}
                          <div className="text-sm text-gray-700 space-y-1 mt-2">
                            {(() => {
                              const grouped = {};
                              selectedPacks.forEach(p => {
                                const key = getPackKey(p);
                                if (!grouped[key]) {
                                  grouped[key] = {
                                    name: p.name,
                                    pack_size: p.pack_size,
                                    rate: p.rate,
                                    gst: p.gst || 0,
                                    count: 0,
                                    loose_qty: 0
                                  };
                                }
                                if (p.pack_size === 0) {
                                  grouped[key].loose_qty += toFiniteNumber(p.loose_qty);
                                } else {
                                  grouped[key].count += 1;
                                }
                              });

                              return Object.values(grouped).map((item, index) => {
                                const isLoose = item.pack_size === 0;
                                const packSizeDisplay = isLoose
                                  ? 'Loose Qty'
                                  : item.pack_size < 1
                                    ? `${(item.pack_size * 1000).toFixed(0)}G`
                                    : `${item.pack_size}${packPopupItem.unit}`;

                                let totalAmount, displayText;

                                if (isLoose) {
                                  const qtyInGrams = item.loose_qty < 1
                                    ? `${(item.loose_qty * 1000).toFixed(0)}G`
                                    : `${item.loose_qty}${packPopupItem.unit}`;
                                  totalAmount = (item.loose_qty * item.rate).toFixed(2);
                                  displayText = `${item.name} ${packSizeDisplay} ${qtyInGrams} × ₹${item.rate}`;
                                } else {
                                  totalAmount = (item.count * item.rate).toFixed(2);
                                  displayText = `${item.name} ${packSizeDisplay} ${item.count}P × ₹${item.rate}`;
                                }

                                const gstText = item.gst > 0 && !isLoose ? ` + ${item.gst}% GST` : '';

                                return (
                                  <div key={index} className="flex items-start justify-between">
                                    <span className="ml-2">
                                      {index + 1}. {displayText} = ₹{totalAmount}{gstText}
                                    </span>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => setPackPopupItem(null)}
                  className="px-5 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={getSelectedQuantity(selectedPacks) <= 0 || isExceedingOrder()}
                  onClick={() => {
                    const totalQty = getSelectedQuantity(selectedPacks);

                    setDispatchData(prev =>
                      prev.map(itm =>
                        itm.id === packPopupItem.id
                          ? {
                            ...itm,
                            dispatchQuantity: totalQty,
                            no_of_packs: selectedPacks.filter(pack => toFiniteNumber(pack.pack_size) > 0).length,
                            selectedPacksData: selectedPacks
                          }
                          : itm
                      )
                    );

                    setPackPopupItem(null);
                  }}
                >
                  Apply Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remarks / Dispatch Detail Popup */}
        {remarksPopupItem && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
              <div className="px-5 py-4 border-b flex justify-between items-center bg-gradient-to-r from-indigo-600 to-indigo-700">
                <h3 className="text-white font-semibold text-lg">
                  Dispatch Details - {remarksPopupItem.name}
                </h3>
                <button
                  onClick={() => setRemarksPopupItem(null)}
                  className="text-white hover:bg-white/20 rounded-full p-1"
                >✕</button>
              </div>

              <div className="p-5 space-y-4">
                <div className="bg-gray-50 border rounded-lg p-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Item</span>
                    <span className="font-semibold text-gray-900">{remarksPopupItem.name}</span>
                  </div>
                  {remarksPopupItem.brand && (
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Brand</span>
                      <span className="font-medium text-green-700">{remarksPopupItem.brand}</span>
                    </div>
                  )}
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Ordered Qty</span>
                    <span className="font-medium">{remarksPopupItem.orderQuantity} {remarksPopupItem.unit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Dispatched Qty</span>
                    <span className="font-medium text-green-700">{remarksPopupItem.dispatchQuantity} {remarksPopupItem.unit}</span>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Dispatch Breakdown</h4>
                  <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-gray-800 leading-relaxed space-y-2">
                    {remarksPopupItem.selectedPacksData && remarksPopupItem.selectedPacksData.length > 0 ? (
                      (() => {
                        const grouped = {};
                        remarksPopupItem.selectedPacksData.forEach(p => {
                          const key = getPackKey(p);
                          if (!grouped[key]) {
                            grouped[key] = { name: p.name, pack_size: p.pack_size, rate: p.rate, gst: p.gst || 0, count: 0, loose_qty: 0 };
                          }
                          if (p.pack_size === 0) {
                            grouped[key].loose_qty += toFiniteNumber(p.loose_qty);
                          } else {
                            grouped[key].count += 1;
                          }
                        });

                        return Object.values(grouped).map((item, index) => {
                          const isLoose = item.pack_size === 0;
                          const packSizeDisplay = isLoose ? 'Loose Qty' : item.pack_size < 1 ? `${(item.pack_size * 1000).toFixed(0)}G` : `${item.pack_size}${remarksPopupItem.unit}`;
                          let totalAmount, displayText;

                          if (isLoose) {
                            const qtyInGrams = item.loose_qty < 1 ? `${(item.loose_qty * 1000).toFixed(0)}G` : `${item.loose_qty}${remarksPopupItem.unit}`;
                            totalAmount = (item.loose_qty * item.rate).toFixed(2);
                            displayText = `${item.name} ${packSizeDisplay} ${qtyInGrams} × ₹${item.rate}`;
                          } else {
                            totalAmount = (item.count * item.rate).toFixed(2);
                            displayText = `${item.name} ${packSizeDisplay} ${item.count}P × ₹${item.rate}`;
                          }

                          const gstText = item.gst > 0 && !isLoose ? ` + ${item.gst}% GST` : '';

                          return (
                            <div key={index} className="font-medium">
                              {index + 1}. {displayText} = ₹{totalAmount}{gstText}
                            </div>
                          );
                        });
                      })()
                    ) : remarksPopupItem.pack_size > 0 && remarksPopupItem.no_of_packs !== "-" && Number(remarksPopupItem.no_of_packs) > 0 ? (
                      <div className="font-medium">
                        {(() => {
                          const displayBrand = remarksPopupItem.brand ||
                            (remarksPopupItem.brandPacks && remarksPopupItem.brandPacks.length > 0
                              ? remarksPopupItem.brandPacks.find(p => p.pack_size === remarksPopupItem.pack_size && p.rate === remarksPopupItem.rate)?.name || 'Unknown Brand'
                              : 'Unknown Brand');
const gst = remarksPopupItem.gst || 0;
const baseAmount = remarksPopupItem.no_of_packs * remarksPopupItem.rate;
const gstAmount = gst > 0 ? (baseAmount * gst / 100) : 0;
const totalAmount = baseAmount + gstAmount;
return `${displayBrand} ${remarksPopupItem.pack_size < 1
  ? `${(remarksPopupItem.pack_size * 1000).toFixed(0)}G`
  : `${remarksPopupItem.pack_size}${remarksPopupItem.unit}`} ${remarksPopupItem.no_of_packs}P × ₹${remarksPopupItem.rate} = ₹${baseAmount.toFixed(2)}${gst > 0 ? ` + ${gst}% GST (₹${gstAmount.toFixed(2)}) = ₹${totalAmount.toFixed(2)}` : ''}`;
                        })()}
                      </div>
                    ) : (
                  <div className="font-medium">
  {(() => {
    const gst = remarksPopupItem.gst || 0;
    const baseAmount = remarksPopupItem.dispatchQuantity * parseFloat(remarksPopupItem.rate || 0);
    const gstAmount = gst > 0 ? (baseAmount * gst / 100) : 0;
    const totalAmount = baseAmount + gstAmount;
    return `${remarksPopupItem.brand || 'Loose Qty'} - ${remarksPopupItem.dispatchQuantity} ${remarksPopupItem.unit} × ₹${remarksPopupItem.rate} = ₹${baseAmount.toFixed(2)}${gst > 0 ? ` + ${gst}% GST (₹${gstAmount.toFixed(2)}) = ₹${totalAmount.toFixed(2)}` : ''}`;
  })()}
</div>
                    )}
                  </div>
                </div>

                {remarksPopupItem.remarks && remarksPopupItem.remarks.trim() !== "" && (
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Additional Remarks</h4>
                    <p className="text-sm text-gray-800">{remarksPopupItem.remarks}</p>
                  </div>
                )}
              </div>

              <div className="px-5 py-3 border-t flex justify-end bg-gray-50">
                <button
                  onClick={() => setRemarksPopupItem(null)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DispatchOrder;
